import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import styled from 'styled-components';
import {
	FieldConfiguration,
	FrontendExternalDataCallConfiguration,
} from '../../api/ConfigApi';
import { AuthData } from '../../context/auth';
import { withPolicyContext } from '../../context/policy';
import { MediaResponse } from '../../types/media';
import {
	FieldGroupCreateRequest,
	FieldGroupsByLocatorMap,
	FieldValues,
	FieldValuesMap,
} from '../../types/policy';
import Field from './fields/Field';
import FieldHeading from './fields/FieldHeading';
import { isFieldAvailableForDisplay } from './fields/util';

const useStyles = makeStyles(() => ({
	grid: {
		paddingTop: '10px',
		paddingRight: '10px',
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
	},
}));

interface Props {
	updateFieldValue: (
		field: FieldConfiguration,
		value: FieldValues,
		usesExternalDataCall?: boolean,
	) => void;
	updateGroupFieldValue: (
		name: string,
		values: FieldValuesMap,
		index: number,
		fieldGroupLocator?: string,
	) => void;
	authData: AuthData;
	currentFieldValues: FieldValuesMap; // current field values are the pending changes that the user is making
	fieldConfigurations: FieldConfiguration[];
	fieldGroupsByLocator: FieldGroupsByLocatorMap;
	fieldValues: FieldValuesMap; // fieldValues is the values from the server
	onAddFieldGroup?: (fieldName: string) => void;
	removeFieldGroup?: (locator: string, index: number, name: string) => void;
	isEditable?: boolean;
	externalDataCalls?: FrontendExternalDataCallConfiguration[];
	tabularDatasourcesHandler?: any;
	userHasWriteAccess?: boolean;
	addFieldGroups?: FieldGroupCreateRequest[]; // for endorsements
	groupIndex?: string; // for group field unique keys
	mediaByLocator?: { [locator: string]: MediaResponse };
	configVersion: string;
}

const FieldEditorForm = (props: Props) => {
	const {
		currentFieldValues,
		fieldConfigurations,
		fieldGroupsByLocator,
		fieldValues,
		isEditable,
		externalDataCalls,
		updateFieldValue,
		updateGroupFieldValue,
		onAddFieldGroup,
		removeFieldGroup,
		addFieldGroups,
		groupIndex,
		mediaByLocator,
		configVersion,
	} = props;

	if (!currentFieldValues) {
		return null;
	}

	if (!fieldConfigurations.length) {
		return null;
	}
	const classes = useStyles();

	const renderField = (field: any, index: number) => {
		if (
			isFieldAvailableForDisplay(
				fieldConfigurations,
				{ ...fieldValues, ...currentFieldValues },
				field.name,
			)
		) {
			let value = currentFieldValues[field.name] ||
				fieldValues[field.name] || [''];
			value = Array.isArray(value) ? value : [value];
			const key = `${field.name}-${groupIndex}-${index}`;
			const fieldWidth = field.type === 'group' ? '12' : field.width || '6';
			const width: any = parseInt(fieldWidth, 10);

			return (
				<React.Fragment key={key}>
					{field.newline || field.type === 'group' ? (
						<Box width='100%' />
					) : null}
					<FieldHeading field={field} />
					<Grid item xs={width} className={`${classes.grid}`}>
						<Field
							value={value}
							field={field}
							isDisabled={!isEditable}
							updateFieldValue={updateFieldValue}
							updateGroupFieldValue={updateGroupFieldValue}
							onAddFieldGroup={onAddFieldGroup}
							removeFieldGroup={removeFieldGroup}
							fieldGroupsByLocator={fieldGroupsByLocator}
							allValues={{ ...fieldValues, ...currentFieldValues }}
							currentFieldValues={currentFieldValues}
							addFieldGroups={addFieldGroups}
							groupIndex={groupIndex}
							mediaByLocator={mediaByLocator}
							externalDataCalls={externalDataCalls}
							fieldConfigurations={fieldConfigurations}
							configVersion={configVersion}
						/>
						{field.type === 'group' && <Break />}
					</Grid>
				</React.Fragment>
			);
		} else {
			return null;
		}
	};
	// Fieldeditorform > Field > [string, group] > Fieldeditorform
	const content = fieldConfigurations.map((field: any, index: number) => {
		return renderField(field, index);
	});

	return <Grid container>{content}</Grid>;
};

const Break = styled.div`
	width: 100%;
	height: 0;
`;

export default withPolicyContext<Props>(FieldEditorForm);
